import clsx from "clsx";
import {
  type KeenSliderInstance,
  type KeenSliderOptions,
  useKeenSlider,
} from "keen-slider/react";
import { useImperativeHandle, type ReactNode, type Ref } from "react";

import "keen-slider/keen-slider.min.css";

import { useLocaleInfo } from "@/hooks/useLocaleInfo";

export type ImageCarouselHandle = {
  readonly instance: KeenSliderInstance | null;
};

export type ImageCarouselProps<ItemShape> = {
  items: ItemShape[];
  renderItem: (item: ItemShape, index: number) => ReactNode;
  getItemKey?: (item: ItemShape, index: number) => string | number;
  slideClassName?: string;
  className?: string;
  onSlideChange?: (index: number) => void;
  sliderRef?: Ref<ImageCarouselHandle>;
} & Partial<KeenSliderOptions>;

export function ImageCarousel<ItemShape>({
  sliderRef,
  items,
  renderItem,
  getItemKey,
  slideClassName,
  className,
  loop = true,
  renderMode = "performance",
  slides,
  disabled = items.length === 0,
  onSlideChange,
  ...restSliderOptions
}: ImageCarouselProps<ItemShape>) {
  const { dir } = useLocaleInfo();

  const renderItems = loop && items.length === 2 ? [...items, ...items] : items;

  const [containerRef, slider] = useKeenSlider<HTMLDivElement>({
    loop,
    renderMode,
    rtl: dir === "rtl",
    disabled,
    slideChanged: onSlideChange
      ? (slider) => onSlideChange(slider.track.details.rel)
      : undefined,
    ...restSliderOptions,
    slides:
      typeof slides === "object"
        ? {
            perView: "auto",
            spacing: 8,
            origin: "center",
            number: renderItems.length,
            ...slides,
          }
        : slides,
  });

  useImperativeHandle(
    sliderRef,
    () => ({
      get instance() {
        return slider.current;
      },
    }),
    [slider],
  );

  if (items.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={clsx("keen-slider", className)}
      data-testid="carousel"
    >
      {renderItems.map((item, index) => (
        <div
          key={getItemKey ? getItemKey(item, index) : index}
          className={clsx("keen-slider__slide", slideClassName)}
          data-testid="carousel-item"
        >
          {renderItem(item, index)}
        </div>
      ))}
    </div>
  );
}
